'use client';
import {
  Fragment,
  HTMLProps,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import PageTitle from '@/components/center/PageTitle';
import UserAvatar from '@/components/user-avatar';
import { UserState, useUserStore } from '@/lib/store/user.store';
import { copyToClipboard, hidePhone } from '@/lib/util/util';
import { Button, Spin, Tag } from 'antd';
import dayjs from 'dayjs';
import { AddressInterface } from '@/lib/type/address.type';
import { useUploadImages } from '@/hooks/useUploadImages';
import { getCenterAddressLink } from '@/lib/util/get-router';

type Props = {
  address: AddressInterface[];
} & HTMLProps<HTMLDivElement>;

const getDefaultAddress = (address: AddressInterface[]) => {
  return address.find((item) => item.is_default === '1');
};
export default memo(function AccountInformation(props: Props) {
  const { userInfo } = useUserStore();
  const [user, setUser] = useState<UserState['userInfo']>(userInfo);
  const [uploadAvatarLoading, setUploadAvatarLoading] = useState(false);

  const { uploadAvatar } = useUploadImages();

  const onUploadAvatar = useCallback(async () => {
    uploadAvatar({
      size: 1024 * 1024,
      setLoading: setUploadAvatarLoading,
      accept: ['image/jpeg', 'image/png', 'image/webp'],
    });
  }, [uploadAvatar]);

  useEffect(() => {
    setUser(userInfo);
  }, [userInfo]);
  return (
    <Fragment>
      <PageTitle title={'账户信息'} />
      <div className="s-flex gap-x-4 mt-4">
        <Spin spinning={uploadAvatarLoading}>
          <div
            className="group relative w-fit h-fit rounded-full overflow-hidden cursor-pointer"
            onClick={onUploadAvatar}
          >
            <UserAvatar size={60} />
            <div className="bg-[rgba(0,0,0,0.3)] absolute hidden duration-300 group-hover:block inset-0">
              <p className="absolute bottom-5 text-xs left-1/2 translate-x-[-50%] text-white whitespace-nowrap">
                编辑头像
              </p>
            </div>
          </div>
        </Spin>
        <div className="b-flex !items-start flex-col gap-y-2">
          <p className="font-bold text-lg">{hidePhone(user?.member?.mobile)}</p>
          <div className="s-flex gap-x-3">
            <Tag color={'#f3e8e1'} className="!text-black">
              {user?.member.memberLevel
                ? user?.member.memberLevel.name
                : '个人用户'}
            </Tag>
            {!user?.member.memberLevel && (
              <Button size="small">申请成为企业用户</Button>
            )}
          </div>
        </div>
      </div>
      <div className="mt-8 s-flex gap-y-4 flex-col !items-start">
        <div className="s-flex gap-x-7">
          <p className="text-themeP">用户名：</p>
          <p>{user?.member?.nickname}</p>
          <Button
            size="small"
            onClick={() => copyToClipboard(user?.member?.nickname)}
          >
            复制
          </Button>
        </div>
        <div className="s-flex gap-x-7">
          <p className="text-themeP">邮箱：</p>
          <p>{user?.member?.email || '暂无'}</p>
          <Button
            size="small"
            onClick={() => copyToClipboard(user?.member?.email)}
          >
            复制
          </Button>
        </div>
        <div className="s-flex gap-x-7">
          <p className="text-themeP">注册日期：</p>
          <p>
            {user?.member?.created_at &&
              dayjs(user.member.created_at * 1000).format('YYYY-MM-DD HH:ss')}
          </p>
        </div>
        <div className="s-flex gap-x-7">
          <p className="text-themeP">发票信息：</p>
          <p>暂无</p>
          <Button size="small">设置</Button>
        </div>
        <div className="s-flex gap-x-7">
          <p className="text-themeP">收货地址：</p>
          <p>
            {!!props?.address?.length
              ? getDefaultAddress(props.address)?.name +
                ' ' +
                getDefaultAddress(props.address)?.details
              : '暂无'}
          </p>
          <Button size="small" href={getCenterAddressLink()}>
            设置
          </Button>
        </div>
      </div>
    </Fragment>
  );
});
